import type { MetricEvent } from '../../types/apiModels'

type GrazingPlanRecord = Pick<MetricEvent, 'id' | 'paddockIds' | 'data'>

export type NonConsecutiveGrazingPlanPair = {
  closedPlanId: string
  openPlanId: string
  paddockIds: string[]
  closedPlanEndDate: string
  openPlanStartDate: string
}

// SARA option ids may arrive as numbers or strings, so they are compared as strings.
const HOLISTIC_GRAZING_TYPE_ID = '0'
const CLOSED_PLAN = { typeId: '1', dateField: 'grazingGeneralPlanEndDate' } as const
const OPEN_PLAN = { typeId: '0', dateField: 'grazingPlanStartDate' } as const

type HolisticPlanKind = typeof CLOSED_PLAN | typeof OPEN_PLAN

// Same date rule as the API, so the warning lists exactly the pairs that block sending to review.
// Anything else, such as the literal "Invalid Date" some stored records carry, counts as missing.
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/

const toUtcDate = (calendarDate: string, dayOffset = 0): Date => {
  const [year, month, day] = calendarDate.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + dayOffset))
}

// Date.UTC rolls impossible days such as 2019-02-30 over into the next month instead of failing.
// Only month and day are compared, as in the API: Date.UTC maps years 0-99 onto 1900-1999.
const isExistingCalendarDate = (calendarDate: string): boolean =>
  toUtcDate(calendarDate).toISOString().slice(5, 10) === calendarDate.slice(5)

const toCalendarDate = (value: unknown): string | null => {
  if (typeof value !== 'string' || !CALENDAR_DATE.test(value)) return null

  const calendarDate = value.slice(0, 10)
  return isExistingCalendarDate(calendarDate) ? calendarDate : null
}

const nextCalendarDay = (calendarDate: string): string =>
  toUtcDate(calendarDate, 1).toISOString().slice(0, 10)

const isHolisticPlanOfKind = (record: GrazingPlanRecord, planKind: HolisticPlanKind): boolean =>
  String(record.data?.grazingTypeId) === HOLISTIC_GRAZING_TYPE_ID &&
  String(record.data?.grazingPlanTypeId) === planKind.typeId

const plansWithDate = (records: GrazingPlanRecord[], planKind: HolisticPlanKind) =>
  records
    .filter(record => isHolisticPlanOfKind(record, planKind))
    .map(record => ({ record, date: toCalendarDate(record.data?.[planKind.dateField]) }))
    .filter(plan => plan.date !== null)

type DatedPlan = ReturnType<typeof plansWithDate>[number]

// Ties keep the first plan, so a paddock is reported at most once per open plan.
const latestClosedPlanByPaddock = (closedPlans: DatedPlan[]): Map<string, DatedPlan> => {
  const latestByPaddock = new Map<string, DatedPlan>()
  closedPlans.forEach(closedPlan => {
    const paddockIds = closedPlan.record.paddockIds ?? []
    paddockIds.forEach(paddockId => {
      const latest = latestByPaddock.get(paddockId)
      if (!latest || closedPlan.date > latest.date) latestByPaddock.set(paddockId, closedPlan)
    })
  })
  return latestByPaddock
}

// An open plan must start the day after the latest closed plan on each paddock it shares; older
// closed plans on that paddock are superseded, so they never make the open plan non-consecutive.
export const findNonConsecutiveGrazingPlans = (
  records: GrazingPlanRecord[] = [],
): NonConsecutiveGrazingPlanPair[] => {
  const closedPlans = plansWithDate(records, CLOSED_PLAN)
  const openPlans = plansWithDate(records, OPEN_PLAN)
  const latestByPaddock = latestClosedPlanByPaddock(closedPlans)

  return closedPlans.flatMap(closedPlan => {
    const expectedOpenPlanStartDate = nextCalendarDay(closedPlan.date)
    const paddockIdsWhereLatest = (closedPlan.record.paddockIds ?? []).filter(
      paddockId => latestByPaddock.get(paddockId) === closedPlan,
    )

    return openPlans.flatMap(openPlan => {
      const openPaddockIds = openPlan.record.paddockIds ?? []
      const sharedPaddockIds = paddockIdsWhereLatest.filter(paddockId =>
        openPaddockIds.includes(paddockId),
      )
      if (!sharedPaddockIds.length || openPlan.date === expectedOpenPlanStartDate) return []

      return [
        {
          closedPlanId: closedPlan.record.id,
          openPlanId: openPlan.record.id,
          paddockIds: sharedPaddockIds,
          closedPlanEndDate: closedPlan.date,
          openPlanStartDate: openPlan.date,
        },
      ]
    })
  })
}
